import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, ArrowRight, Check, Circle, CalendarClock, CheckSquare,
  Camera, Video, Briefcase, Globe, Inbox, User, BookOpen, X,
} from 'lucide-react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useIsMobile } from '../hooks/useResponsive';
import { useWorkspace } from '../hooks/useWorkspace';
import { useLiveRefresh } from '../hooks/useLiveRefresh';
import { Button, Badge, EmptyState, CardGridSkeleton } from '../components/ui';
import { WORKSPACE_INFO, BOARD, classifica, mancanze } from '../workspaces';
import { SegnaliContenuto } from '../components/content';

/**
 * "Il mio lavoro" — bacheca per mestiere (Videomaker / Social Media Manager).
 *
 * Una sola schermata che risponde a "cosa devo fare adesso?":
 *  1. In uscita a breve (contenuti in pubblicazione oggi/domani/nei prossimi giorni)
 *  2. Le mie task aperte
 *  3. La bacheca a colonne, costruita dai contenuti reali del PED (mese corrente + prossimo)
 * Ogni card ha UNA azione primaria (es. "Visual pronto" / "Copy pronto") e si apre col click.
 */

// lucide-react 1.x non ha più le icone dei brand: usiamo icone generiche per piattaforma
const PIATTAFORMA_ICON = { instagram: Camera, tiktok: Video, youtube: Video, linkedin: Briefcase };

function primoDelMese(offset = 0) {
  const d = new Date();
  const m = new Date(d.getFullYear(), d.getMonth() + offset, 1);
  return `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}-01`;
}

function giorniDa(iso) {
  if (!iso) return null;
  const oggi = new Date(); oggi.setHours(0, 0, 0, 0);
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return Math.round((d - oggi) / 86400000);
}

function quando(iso) {
  const g = giorniDa(iso);
  if (g === null) return { label: 'Senza data', color: 'var(--text-dim)' };
  if (g < 0) return { label: `${-g}g fa`, color: 'var(--red)' };
  if (g === 0) return { label: 'Oggi', color: 'var(--yellow)' };
  if (g === 1) return { label: 'Domani', color: 'var(--yellow)' };
  if (g < 7) return { label: `tra ${g}g`, color: 'var(--text-muted)' };
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return { label: d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }), color: 'var(--text-dim)' };
}

function saluto() {
  const h = new Date().getHours();
  return h < 13 ? 'Buongiorno' : h < 18 ? 'Buon pomeriggio' : 'Buonasera';
}

/* ── Card contenuto ───────────────────────────────────────────────────── */
function ContenutoCard({ r, ws, action, busy, onAction, onOpen }) {
  const q = quando(r.data_pubblicazione);
  const PIcon = PIATTAFORMA_ICON[(r.piattaforma || '').toLowerCase()] || Globe;
  const manca = mancanze(ws, r);
  return (
    <div onClick={() => onOpen(r)} role="button" tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') onOpen(r); }}
      style={{
        background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10,
        padding: '11px 12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 7,
        transition: 'border-color 0.15s, transform 0.15s', touchAction: 'manipulation',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-h)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-dim)', minWidth: 0 }}>
        <PIcon size={12} style={{ flexShrink: 0 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {r.cliente_nome || '—'} · {r.formato || 'Post'}
        </span>
        <span style={{ color: q.color, fontWeight: 600, flexShrink: 0 }}>{q.label}</span>
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', lineHeight: 1.35 }}>
        {r.titolo_post || 'Senza titolo'}
      </div>
      {r.hook && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {r.hook}
        </div>
      )}
      <SegnaliContenuto r={r} />
      {/* Una sola cosa in evidenza (la prossima da fare); il resto nel tooltip, per non affollare la card */}
      {manca.length > 0 && (
        <div title={'Manca: ' + manca.join(', ')} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-dim)' }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--yellow)', flexShrink: 0 }} />
          <span>Manca {manca[0]}</span>
          {manca.length > 1 && <span style={{ fontWeight: 600 }}>+{manca.length - 1}</span>}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 2 }}>
        {action && action.kind !== 'open' ? (
          <Button size="sm" variant="primary" icon={Check} loading={busy}
            onClick={e => { e.stopPropagation(); onAction(r, action); }}>
            {action.label}
          </Button>
        ) : action ? (
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); onOpen(r); }}>{action.label}</Button>
        ) : <span />}
        <span style={{ fontSize: 11.5, color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 3 }}>
          Apri <ArrowRight size={12} />
        </span>
      </div>
    </div>
  );
}

/* ── Colonna ──────────────────────────────────────────────────────────── */
function Colonna({ col, righe, ws, busyId, onAction, onOpen }) {
  const [tutte, setTutte] = useState(false);
  const LIMITE = 8;
  const visibili = tutte ? righe : righe.slice(0, LIMITE);
  return (
    <section style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 10, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <header style={{ padding: '2px 4px 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>{col.title}</h3>
          <Badge tone={righe.length ? col.tone : 'neutral'}>{righe.length}</Badge>
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 3, lineHeight: 1.4 }}>{col.hint}</div>
      </header>
      {righe.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-dim)', textAlign: 'center', padding: '18px 8px', border: '1px dashed var(--border)', borderRadius: 10 }}>
          Niente qui
        </div>
      ) : visibili.map(r => (
        <ContenutoCard key={r.id} r={r} ws={ws} action={col.action} busy={busyId === r.id} onAction={onAction} onOpen={onOpen} />
      ))}
      {righe.length > LIMITE && (
        <button onClick={() => setTutte(t => !t)}
          style={{ background: 'none', border: 'none', color: 'var(--cyan)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 6 }}>
          {tutte ? 'Mostra meno' : `Mostra tutti (${righe.length})`}
        </button>
      )}
    </section>
  );
}

/* ── Pagina ───────────────────────────────────────────────────────────── */
export default function Studio() {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { workspace } = useWorkspace();
  const ws = workspace === 'videomaker' ? 'videomaker' : 'social';
  const info = WORKSPACE_INFO[ws];

  const [righe, setRighe] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cliente, setCliente] = useState('');
  const [soloMiei, setSoloMiei] = useState(false);
  // Invito alla guida per chi è nuovo: sparisce chiudendolo o completando la checklist della guida
  const guidaKey = `eden-guida-banner:${user?.id || 'anon'}`;
  const [guidaVista, setGuidaVista] = useState(() => {
    try {
      if (localStorage.getItem(guidaKey) === '1') return true;
      const fatti = JSON.parse(localStorage.getItem(`eden-guida-checklist:${user?.id || 'anon'}`)) || {};
      return Object.values(fatti).filter(Boolean).length >= 9;
    } catch { return false; }
  });
  const chiudiGuida = () => { setGuidaVista(true); try { localStorage.setItem(guidaKey, '1'); } catch {} };
  const [busyId, setBusyId] = useState(null);
  const [tabMobile, setTabMobile] = useState(null);

  const load = useCallback(async (silenzioso = false) => {
    if (!silenzioso) setLoading(true);
    try {
      const [cur, next, t] = await Promise.all([
        api.get('/ped', { params: { mese: primoDelMese(0), con_bozze: true }, silent: silenzioso }),
        api.get('/ped', { params: { mese: primoDelMese(1) }, silent: silenzioso }),
        api.get('/task', { silent: true }).catch(() => ({ data: [] })),
      ]);
      const visti = new Set();
      const tutte = [...cur.data, ...next.data].filter(r => (visti.has(r.id) ? false : visti.add(r.id)));
      setRighe(tutte);
      setTasks(t.data || []);
    } catch {
      // il toast d'errore lo mostra già l'interceptor
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Le modifiche dei colleghi compaiono da sole (ogni 30s, solo con la scheda visibile)
  useLiveRefresh(() => load(true), 30000);

  // "Assegnati a me": prima l'incaricato vero (videomaker_id / responsabile_id, gestiti
  // dal pannello Produzione); in mancanza, il nome scritto a mano in agente_visual / agente_copy.
  const nomeUtente = (user?.nome || '').trim().toLowerCase();
  const campoId = ws === 'videomaker' ? 'videomaker_id' : 'responsabile_id';
  const campoMio = ws === 'videomaker' ? 'agente_visual' : 'agente_copy';
  const eMio = useCallback(r => {
    if (r[campoId]) return String(r[campoId]) === String(user?.id);
    return !!nomeUtente && (r[campoMio] || '').toLowerCase().includes(nomeUtente);
  }, [nomeUtente, campoMio, campoId, user]);
  const haAssegnati = useMemo(() => righe.some(eMio), [righe, eMio]);

  const clienti = useMemo(() => {
    const m = new Map();
    for (const r of righe) if (r.cliente_id) m.set(r.cliente_id, r.cliente_nome || 'Cliente');
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [righe]);

  const filtrate = useMemo(() => righe.filter(r =>
    (!cliente || r.cliente_id === cliente) && (!soloMiei || eMio(r))
  ), [righe, cliente, soloMiei, eMio]);

  const colonne = BOARD[ws];
  const perColonna = useMemo(() => classifica(ws, filtrate), [ws, filtrate]);

  const inUscita = useMemo(() => filtrate
    .filter(r => {
      const g = giorniDa(r.data_pubblicazione);
      return g !== null && g >= 0 && g <= 2 && r.stato !== 'Pubblicato' && r.stato !== 'Scartato';
    })
    .sort((a, b) => String(a.data_pubblicazione).localeCompare(String(b.data_pubblicazione))), [filtrate]);

  const mieTask = useMemo(() => tasks
    .filter(t => t.stato !== 'completata' && t.stato !== 'annullata')
    .filter(t => !t.assegnata_a || String(t.assegnata_a) === String(user?.id))
    .filter(t => { const g = giorniDa(t.data_scadenza); return g === null || g <= 7; })
    .sort((a, b) => String(a.data_scadenza || '9').localeCompare(String(b.data_scadenza || '9'))), [tasks, user]);

  const openRiga = (r) => navigate(`/ped/contenuto/${r.id}`);

  const onAction = async (r, action) => {
    if (action.kind !== 'copy_ok' && action.kind !== 'visual_ok') return openRiga(r);
    setBusyId(r.id);
    try {
      // PATCH: solo il campo cambiato; la risposta è la riga completa (stato ricalcolato)
      const res = await api.patch(`/ped/${r.id}`, { [action.kind]: true });
      setRighe(rs => rs.map(x => (x.id === r.id ? { ...x, ...res.data } : x)));
      toast?.success?.(action.kind === 'visual_ok' ? 'Visual segnato come pronto' : 'Copy segnato come pronto');
    } catch {
      /* toast già mostrato */
    } finally {
      setBusyId(null);
    }
  };

  const completaTask = async (t) => {
    setTasks(ts => ts.map(x => (x.id === t.id ? { ...x, stato: 'completata' } : x)));
    try {
      await api.put(`/task/${t.id}`, { stato: 'completata' });
    } catch {
      setTasks(ts => ts.map(x => (x.id === t.id ? { ...x, stato: t.stato } : x)));
    }
  };

  const daFare = colonne.filter(c => c.action).reduce((n, c) => n + (perColonna[c.key]?.length || 0), 0);
  const colAttiva = tabMobile || colonne.find(c => perColonna[c.key]?.length)?.key || colonne[0].key;

  const selectStyle = {
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, color: 'var(--text)',
    font: 'inherit', fontSize: 13, padding: '0 10px', height: 36, cursor: 'pointer', maxWidth: '100%',
  };

  return (
    <div className="eden-page">
      {/* Intestazione */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: 'var(--cyan-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <info.Icon size={21} color="var(--cyan)" />
          </div>
          <div>
            <h1 style={{ fontSize: 21, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.2 }}>
              {saluto()}{user?.nome ? `, ${user.nome}` : ''}
            </h1>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 3 }}>
              {loading ? 'Carico il tuo lavoro…'
                : daFare === 0 ? `${info.label} · sei in pari, niente in attesa da te.`
                : `${info.label} · ${daFare} ${daFare === 1 ? 'contenuto aspetta' : 'contenuti aspettano'} te.`}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Button size="md" icon={RefreshCw} onClick={() => load()} aria-label="Aggiorna">{!isMobile && 'Aggiorna'}</Button>
        </div>
      </div>

      {ws === 'social' && !guidaVista && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', marginBottom: 14, borderRadius: 12,
          background: 'var(--cyan-dim)', border: '1px solid var(--border)' }}>
          <BookOpen size={18} color="var(--cyan)" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--text)' }}>
            <b>Nuovo qui?</b> <span style={{ color: 'var(--text-muted)' }}>La guida ti porta dal primo giorno alla routine quotidiana in pochi minuti.</span>
          </div>
          <Button size="sm" variant="primary" onClick={() => navigate('/guida')}>Apri la guida</Button>
          <button type="button" onClick={chiudiGuida} aria-label="Non mostrare più" title="Non mostrare più" className="eden-icon-btn" style={{ width: 30, height: 30 }}>
            <X size={15} />
          </button>
        </div>
      )}

      {/* Oggi: in uscita + mie task */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 18 }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
            <CalendarClock size={15} color="var(--yellow)" />
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>In uscita nei prossimi 2 giorni</span>
          </div>
          {inUscita.length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--text-dim)', padding: '6px 0' }}>Nessun contenuto in uscita a breve.</div>
          ) : inUscita.slice(0, 5).map(r => {
            const q = quando(r.data_pubblicazione);
            const pronto = ws === 'videomaker' ? r.visual_ok : r.copy_ok;
            return (
              <div key={r.id} onClick={() => openRiga(r)}
                style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 4px', borderTop: '1px solid var(--border)', cursor: 'pointer' }}>
                {pronto ? <Check size={14} color="var(--green)" /> : <Circle size={14} color="var(--red)" />}
                <span style={{ fontSize: 13, color: 'var(--text)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.titolo_post}
                </span>
                {!isMobile && <span style={{ fontSize: 11.5, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{r.cliente_nome}</span>}
                <span style={{ fontSize: 11.5, fontWeight: 600, color: q.color, whiteSpace: 'nowrap' }}>
                  {q.label}{r.ora_pubblicazione ? ` ${String(r.ora_pubblicazione).slice(0, 5)}` : ''}
                </span>
              </div>
            );
          })}
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, minHeight: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <CheckSquare size={15} color="var(--cyan)" />
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Le mie task</span>
            </div>
            <button onClick={() => navigate('/task')}
              style={{ background: 'none', border: 'none', color: 'var(--cyan)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
              Tutte →
            </button>
          </div>
          {mieTask.length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--text-dim)', padding: '6px 0' }}>Nessuna task in scadenza questa settimana.</div>
          ) : mieTask.slice(0, 5).map(t => {
            const q = quando(t.data_scadenza);
            return (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 4px', borderTop: '1px solid var(--border)' }}>
                <button onClick={() => completaTask(t)} title="Segna completata" aria-label="Segna completata"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, minHeight: 0, color: 'var(--text-dim)', lineHeight: 1 }}>
                  <Circle size={15} />
                </button>
                <span onClick={() => navigate('/task')}
                  style={{ fontSize: 13, color: 'var(--text)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                  {t.titolo}
                </span>
                {t.cliente_nome && !isMobile && <span style={{ fontSize: 11.5, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{t.cliente_nome}</span>}
                <span style={{ fontSize: 11.5, fontWeight: 600, color: q.color, whiteSpace: 'nowrap' }}>{q.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filtri */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-dim)' }}>
          Contenuti · questo mese e il prossimo
        </span>
        <div style={{ flex: 1 }} />
        {haAssegnati && (
          <Button size="sm" icon={User} variant={soloMiei ? 'primary' : 'ghost'} onClick={() => setSoloMiei(v => !v)}>
            Solo assegnati a me
          </Button>
        )}
        <select value={cliente} onChange={e => setCliente(e.target.value)} style={selectStyle} aria-label="Filtra per cliente">
          <option value="">Tutti i clienti</option>
          {clienti.map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}
        </select>
      </div>

      {/* Bacheca */}
      {loading ? (
        <CardGridSkeleton count={4} minWidth={240} height={220} />
      ) : filtrate.length === 0 ? (
        <EmptyState icon={Inbox} title="Nessun contenuto in programma"
          hint="Quando il Piano Editoriale avrà contenuti per questo mese o il prossimo, li troverai qui già divisi per quello che c'è da fare."
          action={<Button variant="primary" onClick={() => navigate('/ped')}>Apri il Piano Editoriale</Button>} />
      ) : isMobile ? (
        <>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 4 }}>
            {colonne.map(c => {
              const on = colAttiva === c.key;
              return (
                <button key={c.key} onClick={() => setTabMobile(c.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flexShrink: 0,
                    padding: '7px 11px', borderRadius: 9, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                    border: `1px solid ${on ? 'var(--cyan)' : 'var(--border)'}`,
                    background: on ? 'var(--cyan-dim)' : 'var(--surface)',
                    color: on ? 'var(--cyan)' : 'var(--text-muted)',
                  }}>
                  {c.title} <span style={{ opacity: 0.8 }}>{perColonna[c.key]?.length || 0}</span>
                </button>
              );
            })}
          </div>
          {colonne.filter(c => c.key === colAttiva).map(c => (
            <Colonna key={c.key} col={c} righe={perColonna[c.key] || []} ws={ws} busyId={busyId} onAction={onAction} onOpen={openRiga} />
          ))}
        </>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${colonne.length}, minmax(220px, 1fr))`, gap: 12, overflowX: 'auto', alignItems: 'start' }}>
          {colonne.map(c => (
            <Colonna key={c.key} col={c} righe={perColonna[c.key] || []} ws={ws} busyId={busyId} onAction={onAction} onOpen={openRiga} />
          ))}
        </div>
      )}
    </div>
  );
}
